import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@mui/material'
import Input from 'components/shared/textFields/Input'
import PageContainer from 'layouts/PageContainer'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { morphForWords } from 'components/shared/processes'
import {
	defaultValues,
	PlannedEventsFormValues,
	usePlannedEventsInfo,
} from 'hook/query/usePlannedEventsInfo'
import ConstantsApiService from 'services/constantsApiService'
import { useAppDispatch } from 'store/hooks'
import { notifyUser } from 'store/notificationsSlice'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import Preloader from 'ui/Preloader'
import { ButtonUI } from 'ui/buttons/ButtonUI'
import ExitWarningModal from 'ui/modal/ExitWarningModal'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { plannedEventsSchema } from './plannedEventsSchema'

const HealthEventsSettingsPage = () => {
	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors, isDirty, isValid, isSubmitting },
	} = useForm<PlannedEventsFormValues>({
		defaultValues,
		resolver: yupResolver(plannedEventsSchema),
		mode: 'onChange',
	})

	const [exitDialogOpen, setExitDialogOpen] = useState<boolean>(false)
	const navigate = useNavigate()
	const dispatch = useAppDispatch()

	const { isLoading } = usePlannedEventsInfo(setValue)

	if (isLoading) {
		return <Preloader mt={15} />
	}

	const onSave = async (data: PlannedEventsFormValues) => {
		try {
			const payload = {
				next_trim_fixed_days: Number(data.next_trim_fixed_days),
			}

			await ConstantsApiService.updateConstants(payload)

			dispatch(
				notifyUser({
					isError: false,
					title: 'Данные успешно сохранены',
					description: 'Изменения применены.',
				}),
			)

			navigate(-1)
		} catch (e) {
			dispatch(
				notifyUser({
					isError: true,
					title: 'Ошибка при сохранении',
					description: e.message ?? 'Что-то пошло не так',
				}),
			)
		}
	}

	return (
		<PageContainer>
			<form onSubmit={handleSubmit(onSave)}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between',
						height: '100%',
						backgroundColor: COLORS.background,
					}}
				>
					<Box display='flex' flexDirection='column' gap={4}>
						<CardStandart
							sx={{
								p: '24px',
								display: 'flex',
								flexDirection: 'column',
								gap: '16px',
							}}
						>
							<TypographyHeader>Обрезка копыт</TypographyHeader>
							<TypographySecondary>
								Пример: Плановая дата обрезки назначается на каждые 100 дней.
								<br />
								Если обрезка была произведена 10.05.2023, плановой датой
								следующей обрезки будет 18.08.2023.
							</TypographySecondary>
							<Box display='flex' alignItems='center' gap='16px' width={'100%'}>
								<TypographyPrimary>
									Формировать плановую дату каждые
								</TypographyPrimary>
								<Controller
									name='next_trim_fixed_days'
									control={control}
									render={({ field }) => (
										<Input
											width='120px'
											height='54px'
											value={field.value}
											onChange={e => {
												const value = e.target.value.replace(/\D/g, '')
												field.onChange(value)
											}}
											isError={!!errors.next_trim_fixed_days}
											helperText={errors.next_trim_fixed_days?.message || ' '}
											endAdornment={
												field.value ? (
													<TypographySecondary14>
														{morphForWords(Number(field.value), [
															'день',
															'дня',
															'дней',
														])}
													</TypographySecondary14>
												) : null
											}
											isMultiline={false}
											inputProps={{
												inputMode: 'numeric',
												pattern: '[0-9]*',
												style: { textAlign: 'center' },
											}}
											placeholder={'Дни'}
											label={''}
										/>
									)}
								/>
							</Box>
						</CardStandart>
					</Box>

					<Box
						display='flex'
						justifyContent='flex-end'
						alignItems='center'
						gap='24px'
						mt={6}
						width={'100%'}
					>
						<TypographySecondary
							sx={{ cursor: 'pointer', textWrap: 'nowrap' }}
							onClick={() => {
								isDirty ? setExitDialogOpen(true) : navigate(-1)
							}}
						>
							Отменить
						</TypographySecondary>
						<ButtonUI
							type='submit'
							disabled={!isDirty || isLoading || !isValid}
						>
							{isSubmitting ? <Preloader color='#fff' /> : 'Сохранить'}
						</ButtonUI>
					</Box>
				</Box>

				<ExitWarningModal
					open={exitDialogOpen}
					onClose={() => setExitDialogOpen(false)}
					onSave={() => setExitDialogOpen(false)}
					onDismiss={() => {
						setExitDialogOpen(false)
						navigate(-1)
					}}
					title={'Выйти без сохранения'}
					description={'Внесенные изменения будут утеряны.'}
					onSaveText={'Вернуться к редактированию'}
					onDismissText={'Выйти без сохранения'}
				/>
			</form>
		</PageContainer>
	)
}

export default HealthEventsSettingsPage
